import type {
	Business,
	BusinessQuery,
	UserNotes
} from '../modules/dashboard/dashboardTypes';
import service, { HttpResponseSuccess } from './service';

const apiService = service(`${process.env.REACT_BACKEND_HOST}`);

interface BusinessesResponse {
	result: Business[];
	count: number;
}

export const getSubordinateLists = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/list/getSubordinateLists', query);

type GetBusinessesListProps = {
	filters: BusinessQuery;
};

export const getBusinessesList = ({
	filters
}: GetBusinessesListProps): Promise<HttpResponseSuccess<BusinessesResponse>> =>
	apiService.get('/api/getBusinesses', { params: filters });

export const getBusinesses = (
	query: BusinessQuery
): Promise<HttpResponseSuccess<BusinessesResponse>> =>
	apiService.get('/api/getBusinesses', { params: query });

export const getBusiness = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/getBusiness', query);

export const setBusinessReminder = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/note/setBusinessReminder', query);

export const deleteBusinessReminder = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/note/deleteBusinessReminder', query);

export const updateNoteForBusiness = (
	businessId: string,
	notes: UserNotes
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.put('/api/note/upsert', {
		businessId,
		...notes
	});

export const postUserGenData = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/businesses/add-data', query);

export const deleteUserGenData = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/businesses/delete-data', query);

export const getMapBoxKey = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.get('/api/businesses/getMapBoxKey');

export const getAllKeys = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.get('/api/getKey/all');

export const getCommunicationCenterBusinesses = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/note/getContactedBusinesses', query);

export type GetGooglePlacesResponse = {
	business_status: string;
	formatted_address: string;
	geometry: {
		location: {
			lat: number;
			lng: number;
		};
		viewport: {
			northeast: {
				lat: number;
				lng: number;
			};
			southwest: {
				lat: number;
				lng: number;
			};
		};
	};
	icon: string;
	icon_background_color: string;
	icon_mask_base_uri: string;
	name: string;
	opening_hours: {
		open_now: false;
	};
	photos: [
		{
			height: number;
			html_attributions: Array<string>;
			photo_reference: string;
			width: number;
		}
	];
	place_id: string;
	plus_code: {
		compound_code: string;
		global_code: string;
	};
	price_level: number;
	rating: number;
	reference: string;
	types: Array<string>;
	user_ratings_total: number;
};

export const getGooglePlaces = (searchString: {
	searchString: string;
}): Promise<HttpResponseSuccess<Array<GetGooglePlacesResponse>>> =>
	apiService.post('/api/location/getGooglePlaces', searchString);

export const requestNewLocation = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/outbound-email/sendAddLocationRequest', query);

export const requestAddLocationFeature = (): Promise<
	HttpResponseSuccess<unknown>
> => apiService.post('/api/outbound-email/sendAddLocationFeatureRequestEmail');

export const getActivities = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/note/getActivities', query);

export const createActivityNote = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/note/createActivityNote', query);

export const getActivityEmailOutbound = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/getOutboundEmail', query);

export const getActivityEmailInbound = (
	query: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/getInboundEmail', query);
